import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router";
import type { AppOutletContext } from "@/src/layouts/AppLayout";
import { Icon } from "@/components/icons";
import { Tooltip } from "@/components/tooltip";
import { Button } from "@/components/button";
import { Breadcrumb } from "@/components/breadcrumb";
import { PdfExportButton } from "@/components/pdf-export-button";
import { PaginationFooter } from "@/components/pagination-footer";
import { useToast } from "@/components/action-feedback";
import { Tabs } from "@/components/tabs";
import { usePaginatedMatches } from "@/components/use-paginated-matches";
import {
  SINGLE_SCREENING_RESULTS_COLUMNS_META,
  ADV_MEDIA_RESULTS_COLUMNS_META,
  ResultsColumnHeaders,
  ResultsTable,
  useSingleScreeningResultsColumnPrefs,
  useSingleScreeningAdvMediaColumnPrefs,
  useResultsSort,
  type ResultsColumnId,
} from "./results-table";
import { TableColumnsMenu } from "@/components/table-columns-menu";
import { MatchDetail } from "./match-detail";
import { ENTITY_TYPES, type EntityType } from "@/components/filter-cards";
import type { Match } from "../_data/types";
import { getBatch } from "../../_data/batches";
import {
  exportSearchById,
  fetchAdvMedia,
  fetchSanctions,
  resolveActiveCollections,
  type ScreeningSearchInput,
} from "@/src/services/screening_service";
import { addMonitoring } from "@/src/services/monitoring_service";
import { toUserMessage } from "@/src/services/error_messages";
import { captureEvent } from "@/src/lib/posthog";

type ResultsTab = "watchlists" | "adverseMedia";

/**
 * Build the screening search payload from the shareable URL state (`?q=` plus
 * the filter params the search surface writes on submit). Invalid/out-of-range
 * values fall back to safe defaults; the service re-validates with Zod.
 */
function buildSearchInput(query: string, params: URLSearchParams): ScreeningSearchInput {
  const confRaw = Number(params.get("conf"));
  const confidenceScore = Number.isFinite(confRaw) && confRaw >= 80 && confRaw <= 99 ? confRaw : 88;
  const type = params.get("type");
  const entityType = (ENTITY_TYPES as readonly string[]).includes(type ?? "")
    ? (type as EntityType)
    : null;
  const wl = params.get("wl");
  const country = params.get("country");
  return {
    query,
    identifier: params.get("identifier") ?? "",
    confidenceScore,
    entityType,
    dob: params.get("dob") ?? "",
    watchlists: wl ? wl.split(",").filter(Boolean) : [],
    countries: country ? country.split(",").filter(Boolean) : [],
  };
}

export function ResultsToolbar({ query, batchId }: { query: string; batchId?: string }) {
  const batch = batchId ? getBatch(batchId) : undefined;
  const [params] = useSearchParams();
  const ctx = useOutletContext<AppOutletContext | null>();
  const enableMonitoring = ctx?.enableMonitoring ?? true;
  // Identifier-only searches carry no name, so fall back to the identifier as
  // the current breadcrumb segment rather than rendering an empty crumb.
  const label = query || (params.get("identifier") ?? "").trim();
  const [isSearching, setIsSearching] = useState(true);
  useEffect(() => {
    function onSearchState(e: Event) {
      setIsSearching((e as CustomEvent<{ isLoading: boolean }>).detail.isLoading);
    }
    window.addEventListener("screening:search-state", onSearchState);
    return () => window.removeEventListener("screening:search-state", onSearchState);
  }, []);
  // Mirrors the Search button: once a PDF has been requested for the current
  // search, Export is disabled (with a tooltip) until a new search runs.
  const [exportRequested, setExportRequested] = useState(false);
  useEffect(() => {
    function onExportState(e: Event) {
      setExportRequested((e as CustomEvent<{ requested: boolean }>).detail.requested);
    }
    window.addEventListener("screening:export-state", onExportState);
    return () => window.removeEventListener("screening:export-state", onExportState);
  }, []);
  // Phone: stack breadcrumb on top of buttons (one column, gap-2) so the
  // breadcrumb gets the full row and doesn't wrap into a cramped two-line
  // shape. From sm+ they share a row with the buttons pushed right.
  return (
    <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-1 items-center">
        {batch ? (
          <Breadcrumb
            backHref={`/batch/${batch.id}`}
            segments={[
              { label: "Batch Screening", href: "/batch" },
              { label: batch.filename, href: `/batch/${batch.id}` },
              { label, current: true },
            ]}
          />
        ) : (
          // A recent search opened from the dashboard (?runAt=…) used to crumb
          // "Dashboard / <name>", which contradicted the sidebar — it highlights
          // Single Screening for /screening-portal whatever the entry point was
          // (DEV-2294). The crumb tracks the route, not where you came from.
          <Breadcrumb
            backHref="/screening-portal"
            segments={[
              { label: "Single Screening", href: "/screening-portal" },
              { label, current: true },
            ]}
          />
        )}
      </div>
      {/* Export the current search's PDF report. The button lives here, but the
          selected match + export handler live in MatchesPanel (sibling), so it
          bridges via a window event — the same pattern the empty-state "Clear
          filters" button uses. The old "Select" (bulk multi-select) was a dead
          placeholder with no backend behind it — removed until a bulk contract
          exists. Hidden on phone where it'd crowd the breadcrumb; shown sm+.

          NOT shown in the batch context: the report endpoint keys on a per-search
          query UUID, which batch detail rows don't carry — so Export there always
          failed. (A dedicated batch-report endpoint is a backend question for
          Héctor.) Single + historic keep it: live search exports immediately, and
          a recent search without a usable report id surfaces an honest "not
          available" instead of a 404 (see recent_searches_service). */}
      {!batch && (
        <div className="hidden items-center gap-1 sm:flex">
          {enableMonitoring && <AddToMonitoringButton query={query} isSearching={isSearching} />}
          <ExportPdfButton isSearching={isSearching} exportRequested={exportRequested} />
        </div>
      )}
    </div>
  );
}

/**
 * Creates a monitoring entry from the CURRENT SEARCH's values (name/identifier
 * + filters read straight off the URL, mirroring buildSearchInput below) —
 * not a specific match row (that's addMatchToMonitoring, used per-row). On
 * success, navigates to the monitoring list so the user sees the new entry.
 */
function AddToMonitoringButton({ query, isSearching }: { query: string; isSearching: boolean }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [adding, setAdding] = useState(false);
  const showToast = useToast();
  // Mirrors the Export PDF gate: once this exact search has been added,
  // disable re-adding until the query or a filter actually changes — also
  // closes the race where a fast double-click could fire two adds before
  // the post-success navigate() takes effect.
  const [addedSearchKey, setAddedSearchKey] = useState<string | null>(null);
  const currentSearchKey = params.toString();
  const alreadyAdded = addedSearchKey !== null && addedSearchKey === currentSearchKey;

  async function handleClick() {
    if (adding || alreadyAdded) return;
    setAdding(true);

    const identifier = (params.get("identifier") ?? "").trim();
    const confRaw = Number(params.get("conf"));
    const confidenceScore =
      Number.isFinite(confRaw) && confRaw >= 80 && confRaw <= 99 ? confRaw : 88;
    // Single Screening always submits `type` (either the default Individual+
    // Organization pair, or a single explicit type) — only a lone type maps
    // to AddMonitoringInput.entityType, which takes just one value.
    const types = (params.get("type") ?? "").split(",").filter(Boolean);
    const entityType =
      types.length === 1 && (ENTITY_TYPES as readonly string[]).includes(types[0])
        ? (types[0] as EntityType)
        : undefined;
    const watchlists = (params.get("wl") ?? "").split(",").filter(Boolean);
    const countries = (params.get("country") ?? "").split(",").filter(Boolean);

    const res = await addMonitoring({
      name: query.trim() || identifier,
      identifier: identifier || undefined,
      dob: params.get("dob") || undefined,
      countries: countries.length > 0 ? countries : undefined,
      entityType,
      watchlists: watchlists.length > 0 ? watchlists : undefined,
      confidenceScore,
    });

    setAdding(false);
    if (!res.ok) {
      showToast({ tone: "error", text: toUserMessage(res.error) });
      return;
    }
    setAddedSearchKey(currentSearchKey);
    navigate("/monitoring-portal");
  }

  const button = (
    <Button
      hierarchy="secondary"
      size="sm"
      rightIcon={<Icon name="bookmark_add" size={20} />}
      onClick={() => void handleClick()}
      disabled={isSearching || adding || alreadyAdded}
    >
      Add to monitoring
    </Button>
  );

  return (
    <>
      {isSearching || !alreadyAdded ? (
        button
      ) : (
        <Tooltip title="Run a new search to add it to monitoring again" align="end">
          {button}
        </Tooltip>
      )}
    </>
  );
}

function ExportPdfButton({
  isSearching,
  exportRequested,
}: {
  isSearching: boolean;
  exportRequested: boolean;
}) {
  return (
    <PdfExportButton
      onClick={() => window.dispatchEvent(new CustomEvent("screening:export-pdf"))}
      disabled={isSearching || exportRequested}
      disabledTooltip={
        !isSearching && exportRequested ? "Run a new search to export again" : undefined
      }
    />
  );
}

/**
 * Self-contained matches surface. Owns its sort, selection, loading state, and
 * detail-panel state, and runs the search through `screeningService` (the
 * ADR-010 swap point) using the query + filter params from the URL.
 *
 * Watchlists and Adverse Media are fetched, paginated, and displayed
 * independently (two `usePaginatedMatches` instances) — tabs switch between
 * them, and only appear when the search actually covers both collections
 * (see `resolveActiveCollections`); when it covers just one, that one table
 * renders directly.
 *
 * Mount it with `key={query}` from a parent to restart the skeleton on every
 * new query (filter-only changes re-run via the effect without a remount, so
 * sort + column prefs survive).
 */
export function MatchesPanel({ query }: { query: string }) {
  const [params] = useSearchParams();
  const location = useLocation();
  const locationState = location.state as {
    historicalMatches?: Match[];
    historicalSearchId?: string;
  } | null;
  const historicalMatches = locationState?.historicalMatches;
  const historicalSearchId = locationState?.historicalSearchId;
  const isHistorical = historicalMatches !== undefined;
  const showToast = useToast();
  // Mirrors the Search button: once a PDF has been requested for the current
  // search, ResultsToolbar's Export button disables until a new search runs.
  const [exportRequested, setExportRequested] = useState(false);
  const [sort, setSort] = useResultsSort();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ResultsTab>("watchlists");

  const searchInput = useMemo(() => buildSearchInput(query, params), [query, params]);
  const activeCollections = useMemo(() => resolveActiveCollections(searchInput), [searchInput]);
  // Bumped by the error state's "Try again" button to force both paginated
  // hooks to refetch page 1 without needing an imperative runSearch().
  const [retryNonce, setRetryNonce] = useState(0);
  const resetKey = useMemo(() => ({ searchInput, retryNonce }), [searchInput, retryNonce]);

  const [historicalSanctions, historicalMedia] = useMemo(() => {
    if (!historicalMatches) return [[] as Match[], [] as Match[]];
    const sorted = [...historicalMatches].sort((a, b) => b.score - a.score);
    return [
      sorted.filter((m) => m.collection !== "adverse_media"),
      sorted.filter((m) => m.collection === "adverse_media"),
    ];
  }, [historicalMatches]);

  // Live mode: which collections apply comes from the selected watchlists
  // (resolveActiveCollections) — a watchlist that wasn't searched stays
  // hidden even if it would have returned zero results anyway. Historical
  // replay has no watchlist selection to inspect, so it falls back to
  // "whichever collection actually has rows".
  const hasSanctions = isHistorical
    ? historicalSanctions.length > 0
    : activeCollections.ok && activeCollections.value.hasSanctions;
  const hasAdvMedia = isHistorical
    ? historicalMedia.length > 0
    : activeCollections.ok && activeCollections.value.hasAdvMedia;

  // Combined searches link both requests so the backend can build one PDF
  // report covering both collections.
  const linkedSearchReference = useMemo(
    () => (!isHistorical && hasSanctions && hasAdvMedia ? crypto.randomUUID() : undefined),
    [searchInput, isHistorical, hasSanctions, hasAdvMedia],
  );
  const sanctionsInput = activeCollections.ok
    ? activeCollections.value.sanctionsInput
    : searchInput;
  const adverseMediaInput = activeCollections.ok
    ? activeCollections.value.adverseMediaInput
    : searchInput;

  // The search-query uuid a collection's OWN response carries — sibling of
  // `matches`, present even on a zero-result search. Captured as a side
  // channel here (rather than threaded through usePaginatedMatches's generic
  // {matches,count,next} contract) since it's a screening-specific concern.
  const [sanctionsSearchId, setSanctionsSearchId] = useState<string | undefined>(undefined);
  const [advMediaSearchId, setAdvMediaSearchId] = useState<string | undefined>(undefined);

  const fetchSanctionsPage = useCallback(
    async (page: number, signal?: AbortSignal) => {
      const res = await fetchSanctions(sanctionsInput, page, signal, linkedSearchReference);
      if (res.ok) setSanctionsSearchId(res.value.searchId);
      return res;
    },
    [sanctionsInput, linkedSearchReference],
  );
  const fetchAdvMediaPage = useCallback(
    async (page: number, signal?: AbortSignal) => {
      const res = await fetchAdvMedia(adverseMediaInput, page, signal, linkedSearchReference);
      if (res.ok) setAdvMediaSearchId(res.value.matches[0]?.searchId);
      return res;
    },
    [adverseMediaInput, linkedSearchReference],
  );

  const sanctionsPrefs = useSingleScreeningResultsColumnPrefs();
  const advMediaPrefs = useSingleScreeningAdvMediaColumnPrefs();
  const [sanctionsPage, setSanctionsPage] = useState(1);
  const [advMediaPage, setAdvMediaPage] = useState(1);

  const sanctionsResult = usePaginatedMatches(
    !isHistorical && hasSanctions ? fetchSanctionsPage : null,
    resetKey,
    sanctionsPage,
    sanctionsPrefs.rowsPerPage,
  );
  const advMediaResult = usePaginatedMatches(
    !isHistorical && hasAdvMedia ? fetchAdvMediaPage : null,
    resetKey,
    advMediaPage,
    advMediaPrefs.rowsPerPage,
  );

  const sanctionsMatches = isHistorical ? historicalSanctions : sanctionsResult.matches;
  const advMediaMatches = isHistorical ? historicalMedia : advMediaResult.matches;
  const sanctionsCount = isHistorical ? historicalSanctions.length : sanctionsResult.count;
  const advMediaCount = isHistorical ? historicalMedia.length : advMediaResult.count;

  // Tabs reflect actual results, not just search intent — a collection that
  // was searched but came back empty doesn't get its own (empty) tab; it
  // falls through to whichever collection actually has rows.
  const showTabs = sanctionsCount > 0 && advMediaCount > 0;
  const effectiveTab: ResultsTab = showTabs
    ? activeTab
    : advMediaCount > 0
      ? "adverseMedia"
      : "watchlists";

  const isLoading =
    !isHistorical &&
    ((hasSanctions && sanctionsResult.isLoading) || (hasAdvMedia && advMediaResult.isLoading));
  const errorMsg = isHistorical
    ? null
    : !activeCollections.ok
      ? activeCollections.error.message
      : ((hasSanctions ? sanctionsResult.error : null) ??
        (hasAdvMedia ? advMediaResult.error : null));

  // Snap back to page 1 whenever a new search runs or the active tab's
  // page-size changes — NOT on every `matches` growth, which would otherwise
  // fire on every lazily-loaded page and undo the user's own navigation.
  useEffect(() => setSanctionsPage(1), [searchInput, sanctionsPrefs.rowsPerPage]);
  useEffect(() => setAdvMediaPage(1), [searchInput, advMediaPrefs.rowsPerPage]);

  useEffect(() => {
    setExportRequested(false);
  }, [searchInput]);

  // One "found N matches" toast per completed search (both applicable
  // collections resolved), mirroring the old single-fetch toast. Historical
  // replay shows a fixed list instantly and never toasts (unchanged).
  const wasLoadingRef = useRef(isLoading);
  useEffect(() => {
    if (!isHistorical && wasLoadingRef.current && !isLoading && !errorMsg) {
      const count = sanctionsCount + advMediaCount;
      showToast(
        count > 0
          ? { tone: "success", text: `Found ${count} match${count === 1 ? "" : "es"}` }
          : { tone: "success", text: "No matches found for this search" },
      );
    }
    wasLoadingRef.current = isLoading;
  }, [isLoading, isHistorical, errorMsg, sanctionsCount, advMediaCount, showToast]);

  const wasLoadingViewRef = useRef(isLoading);
  const viewedHistoricalRef = useRef(false);
  useEffect(() => {
    const justSettled = wasLoadingViewRef.current && !isLoading;
    wasLoadingViewRef.current = isLoading;
    if (errorMsg || !(justSettled || (isHistorical && !viewedHistoricalRef.current))) return;
    viewedHistoricalRef.current = true;
    captureEvent("Viewed Single Screening Results", {
      match_count: sanctionsCount + advMediaCount,
      sanctions_count: sanctionsCount,
      adverse_media_count: advMediaCount,
      is_historical: isHistorical,
    });
  }, [isLoading, errorMsg, isHistorical, sanctionsCount, advMediaCount]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("screening:export-state", { detail: { requested: exportRequested } }),
    );
  }, [exportRequested]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("screening:search-state", { detail: { isLoading } }));
  }, [isLoading]);

  const activeMatches = effectiveTab === "adverseMedia" ? advMediaMatches : sanctionsMatches;

  // Auto-select the first match of the active tab so the detail card is
  // always populated; re-fires on tab switch since `activeMatches` changes.
  useEffect(() => {
    if (selectedId && activeMatches.some((m) => m.id === selectedId)) return;
    setSelectedId(activeMatches.length > 0 ? activeMatches[0].id : null);
  }, [activeMatches, selectedId]);

  const selectedIndex = selectedId ? activeMatches.findIndex((m) => m.id === selectedId) : -1;
  const selected = selectedIndex >= 0 ? activeMatches[selectedIndex] : null;

  function step(delta: number) {
    if (selectedIndex < 0) return;
    const next = selectedIndex + delta;
    if (next < 0 || next >= activeMatches.length) return;
    setSelectedId(activeMatches[next].id);
  }

  const handleExport = useCallback(
    async (effectiveSearchId: string) => {
      try {
        const res = await exportSearchById(effectiveSearchId);
        if (res.ok) setExportRequested(true);
        showToast(
          res.ok
            ? {
                tone: "success",
                text: "Preparing the PDF report — it'll be ready in notifications shortly",
              }
            : { tone: "error", text: toUserMessage(res.error) },
        );
      } catch {
        showToast({ tone: "error", text: "Couldn't generate the PDF report" });
      }
    },
    [showToast],
  );

  // The report is per-search, not per-tab — the search's own id works even
  // with zero matches (sanctions favored since it's the report's anchor when
  // both collections were searched together).
  const topLevelSearchId = isHistorical
    ? historicalSearchId
    : (sanctionsSearchId ?? advMediaSearchId);

  useEffect(() => {
    function onExportRequest() {
      const effectiveSearchId = isHistorical
        ? (selected?.searchId ?? topLevelSearchId)
        : topLevelSearchId;
      if (effectiveSearchId) void handleExport(effectiveSearchId);
      else showToast({ tone: "error", text: "Export isn't available for this result" });
    }
    window.addEventListener("screening:export-pdf", onExportRequest);
    return () => window.removeEventListener("screening:export-pdf", onExportRequest);
  }, [isHistorical, selected, topLevelSearchId, handleExport, showToast]);

  const totalCount = sanctionsCount + advMediaCount;
  const showResults = !isLoading && !errorMsg && totalCount > 0;
  const showEmpty = !isLoading && !errorMsg && totalCount === 0;
  const totalDisplayCount = isLoading ? "…" : totalCount;

  const isAdvMediaTab = effectiveTab === "adverseMedia";
  const activePrefs = isAdvMediaTab ? advMediaPrefs : sanctionsPrefs;
  const activeCount = isAdvMediaTab ? advMediaCount : sanctionsCount;
  const activePage = isAdvMediaTab ? advMediaPage : sanctionsPage;
  const setActivePage = isAdvMediaTab ? setAdvMediaPage : setSanctionsPage;
  const activePageCount = Math.max(1, Math.ceil(activeCount / activePrefs.rowsPerPage));
  const safeActivePage = Math.min(activePage, activePageCount);

  const individuals = sanctionsMatches.filter((m) => m.type === "Individual").length;
  const organizations = sanctionsMatches.filter((m) => m.type === "Organization").length;
  const others = sanctionsMatches.filter(
    (m) => m.type === "Vessel" || m.type === "Aircraft" || m.type === "Country",
  ).length;

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        {showTabs ? (
          <Tabs
            value={activeTab}
            onChange={(id) => setActiveTab(id as ResultsTab)}
            tabs={[
              { id: "watchlists", label: `Watchlists (${sanctionsCount})` },
              { id: "adverseMedia", label: `Adverse Media (${advMediaCount})` },
            ]}
          />
        ) : (
          <h3 className="text-h4 text-black">Matches ({totalDisplayCount})</h3>
        )}
        {showResults && (
          <TableColumnsMenu
            columns={
              isAdvMediaTab ? ADV_MEDIA_RESULTS_COLUMNS_META : SINGLE_SCREENING_RESULTS_COLUMNS_META
            }
            hidden={activePrefs.hidden}
            onToggle={(id) => activePrefs.toggleHidden(id as ResultsColumnId)}
            onReset={activePrefs.reset}
          />
        )}
      </div>

      {isLoading && (
        <div className="flex flex-col overflow-hidden rounded-sm border border-grey-200 bg-white">
          <SkeletonRows />
        </div>
      )}

      {errorMsg && <ErrorState message={errorMsg} onRetry={() => setRetryNonce((n) => n + 1)} />}

      {showEmpty && (
        <div className="flex flex-col overflow-hidden rounded-sm border border-grey-200 bg-white">
          <NothingToShow activeFilters={describeActiveFilters(searchInput)} />
        </div>
      )}

      {showResults && (
        <>
          {/* One scroller drives both axes: header + rows share a single
              min-width wrapper so columns never drift and there's no double
              scrollbar; the header stays pinned on vertical scroll. The
              pagination footer sits outside the scroller, so it's always
              visible and never scrolls horizontally. */}
          <div className="overflow-hidden rounded-sm border border-grey-200 bg-white shadow-flat">
            <div className="max-h-105 overflow-auto">
              <div className="min-w-260">
                <div className="sticky top-0 z-10">
                  <ResultsColumnHeaders
                    sort={sort}
                    onSort={setSort}
                    order={activePrefs.order}
                    hidden={activePrefs.hidden}
                    onReorder={activePrefs.setOrder}
                    widths={activePrefs.widths}
                    onResizeColumn={activePrefs.setWidth}
                  />
                </div>
                <ResultsTable
                  matches={activeMatches}
                  sort={sort}
                  order={activePrefs.order}
                  hidden={activePrefs.hidden}
                  onSelect={setSelectedId}
                  selectedId={selectedId}
                  widths={activePrefs.widths}
                  page={safeActivePage}
                  rowsPerPage={activePrefs.rowsPerPage}
                />
              </div>
            </div>
            <PaginationFooter
              total={activeCount}
              totalLabel={isAdvMediaTab ? "articles" : "matches"}
              {...(isAdvMediaTab ? {} : { individuals, organizations, others })}
              page={safeActivePage}
              pageCount={activePageCount}
              onPageChange={setActivePage}
              rowsPerPage={activePrefs.rowsPerPage}
              onRowsPerPageChange={(n) => {
                activePrefs.setRowsPerPage(n);
                setActivePage(1);
              }}
            />
          </div>

          {selected && (
            <MatchDetail
              embedded
              showEntityMeta
              match={selected}
              position={{ current: selectedIndex + 1, total: activeMatches.length }}
              onPrev={selectedIndex > 0 ? () => step(-1) : undefined}
              onNext={
                selectedIndex >= 0 && selectedIndex < activeMatches.length - 1
                  ? () => step(1)
                  : undefined
              }
            />
          )}
        </>
      )}
    </section>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-4 rounded-sm border border-grey-200 bg-white px-4 py-16 text-center">
      <div className="flex size-12 items-center justify-center rounded-sm bg-grey-100 text-red-600">
        <Icon name="error" size={24} />
      </div>
      <div className="flex max-w-110 flex-col gap-1.5">
        <h3 className="text-h4 text-black">Couldn’t load matches</h3>
        <p className="text-small text-grey-900">{message}</p>
      </div>
      <Button
        hierarchy="secondary"
        size="sm"
        leftIcon={<Icon name="refresh" size={20} />}
        onClick={onRetry}
      >
        Try again
      </Button>
    </div>
  );
}

function SkeletonRows() {
  // 6 placeholder rows roughly matching the result-row shape
  // (score chip, name, country, dob, type, source). `animate-pulse` is
  // Tailwind's built-in 2s opacity fade — picked because it doesn't need
  // any keyframes outside of what Tailwind ships by default.
  return (
    <div role="status" aria-label="Loading matches" className="flex flex-col">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="flex h-12 animate-pulse items-center gap-3 border-b border-grey-100 px-3"
          style={{ animationDelay: `${i * 80}ms` }}
        >
          <div className="h-4 w-14 rounded-sm bg-grey-200" />
          <div className="h-4 flex-1 max-w-65 rounded-sm bg-grey-200" />
          <div className="h-4 w-32 rounded-sm bg-grey-200" />
          <div className="h-4 w-20 rounded-sm bg-grey-200" />
          <div className="h-4 w-24 rounded-sm bg-grey-200" />
          <div className="h-4 w-20 rounded-sm bg-grey-200" />
          <div className="h-4 w-16 rounded-sm bg-grey-200" />
        </div>
      ))}
    </div>
  );
}

/** Builds a human-readable list of restrictive filters active on the URL —
 *  the things that could realistically exclude an otherwise-matching name.
 *  Watchlists/DOB are omitted because they don't filter today (Hector's
 *  swap point); listing them would mislead the user into a false cause. */
function describeActiveFilters(input: ScreeningSearchInput): string[] {
  const out: string[] = [];
  if (input.confidenceScore > 88) out.push(`Confidence ≥ ${input.confidenceScore}%`);
  if (input.entityType) out.push(`Entity type: ${input.entityType}`);
  // Date of birth + sanctions lists were missing here, so a date-only (or
  // list-only) filter that zeroed the results left hasFilters=false and hid the
  // reset CTA (QA 2026-06-16). Count every filter the search surface can set.
  if (input.dob) out.push(`Date of birth: ${input.dob}`);
  if (input.watchlists && input.watchlists.length > 0) {
    out.push(`Sanctions lists: ${input.watchlists.length}`);
  }
  if (input.countries && input.countries.length > 0) {
    out.push(`Country: ${input.countries.join(", ")}`);
  }
  return out;
}

function NothingToShow({ activeFilters }: { activeFilters: string[] }) {
  const hasFilters = activeFilters.length > 0;
  function clearFilters() {
    // SearchSurface (sibling component, owns filter state) listens for this
    // event and calls its own handleClear — same reset path the dark
    // "Clear" button in the search-parameters bar uses.
    window.dispatchEvent(new CustomEvent("screening:clear-filters"));
  }
  return (
    <div className="flex flex-1 flex-col items-center gap-4 px-4 pb-8 pt-30">
      <div className="flex w-40 flex-col gap-1.5">
        <div className="h-4 w-full rounded-sm bg-grey-200" />
        <div className="h-4 w-full rounded-sm bg-grey-200" />
        <div className="h-4 w-full rounded-sm bg-grey-200" />
      </div>
      <div className="flex max-w-90 flex-col items-center gap-1.5">
        <h3 className="text-h4 text-black">No matches</h3>
        <p className="text-center text-small text-grey-900">
          Try a different name or adjust your filters.
        </p>
        {/* The active-filter list + reset CTA aren't drawn in Figma 15281-38243,
            but are kept (when filters are active) as a deliberate utility — a
            prior QA fix so a filter that zeroes the results still offers a reset. */}
        {hasFilters && (
          <>
            <p className="text-center text-tiny text-grey-900">
              Active: {activeFilters.join(" · ")}
            </p>
            <Button
              hierarchy="secondary"
              size="sm"
              leftIcon={<Icon name="refresh" size={20} />}
              onClick={clearFilters}
            >
              Clear filters
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
